/** Booking rolls in (admin_handler's booking.rs). Admin only. */
import { api } from './client';
import type { NegativesPlan, Roll, RollFormat, RollKind, RollProcess } from '../types/roll';

/** One development or scanning line of the Shopify order. */
export interface BookingLine {
  line_item_id: string;
  kind: RollKind;
  process?: RollProcess;
  format?: RollFormat;
  /** How many rolls the customer paid for. */
  quantity: number;
  title: string;
  variant_title?: string;
  special_instructions?: string;
  /** The order also ships something else (film, say). */
  other_shippable_lines: boolean;
  /** Rolls already booked in against this line. */
  booked: number;
}

/** Whether booking in would email the customer, and if not, why. */
export type ArrivalEmailStatus = 'will_send' | 'already_sent' | 'no_email' | 'not_enabled';

export interface Booking {
  order: {
    order_id: string;
    order_number: string;
    order_date: string;
    customer_id: string;
    customer_name: string | null;
    has_email: boolean;
    shipping_method: string | null;
    negatives_option: NegativesPlan | null;
    booked_in_at: string | null;
    cancelled_at: string | null;
    scan_ready: boolean;
    walk_in: boolean;
  };
  lines: BookingLine[];
  /** The order's lab rolls, oldest first. */
  rolls: Roll[];
  suggested_plan: NegativesPlan | null;
  arrival_email: ArrivalEmailStatus;
  /** This customer's negatives held from other orders: they can go with this one. */
  held_negatives?: HeldNegative[];
}

export interface HeldNegative {
  order_id: string;
  order_number: string | null;
  roll_id: string;
  roll_name: string;
  held_until: string | null;
  overdue: boolean;
}

/** A roll's details as booking in and corrections send them. */
export interface RollDetailsInput {
  lab_roll_number: string | null;
  film_stock_id: string;
  kind: RollKind;
  /** Null takes the film stock's process. */
  process: RollProcess | null;
  format: RollFormat;
}

export interface BookInRoll extends RollDetailsInput {
  /** Chosen by the page, so a retried booking finds the same rolls. */
  roll_id: string;
  line_item_id: string | null;
}

export interface BookInRequest {
  rolls: BookInRoll[];
  negatives_plan: NegativesPlan;
  send_arrival_email: boolean;
  allow_duplicate_numbers?: boolean;
}

export type ArrivalEmailOutcome = 'sent' | 'already_sent' | 'no_email' | 'not_enabled' | 'failed' | 'not_requested';

export interface BookInResponse {
  rolls: { roll_id: string; outcome: 'created' | 'exists' }[];
  arrival_email: ArrivalEmailOutcome;
}

/** A 409 when a lab number was used on another roll in the last 30 days. */
export interface LabNumberConflicts {
  error: string;
  warning: 'lab_number_recent';
  conflicts: { number: string; order_number: string; order_id: string; days_ago: number }[];
}

export function isLabNumberConflicts(body: unknown): body is LabNumberConflicts {
  return !!body && typeof body === 'object' && 'warning' in body && body.warning === 'lab_number_recent';
}

export interface RollDetailsRequest extends RollDetailsInput {
  negatives_plan?: NegativesPlan;
  allow_duplicate_numbers?: boolean;
}

const orderPath = (orderId: string) => `/api/v1/admin/orders/${encodeURIComponent(orderId)}`;

export const booking = {
  get: (orderId: string) => api.get<Booking>(`${orderPath(orderId)}/booking`),
  bookIn: (orderId: string, request: BookInRequest) => api.post<BookInResponse>(`${orderPath(orderId)}/book-in`, request),
  updateDetails: (orderId: string, rollId: string, request: RollDetailsRequest) =>
    api
      .put<{ roll: Roll }>(`${orderPath(orderId)}/rolls/${encodeURIComponent(rollId)}/details`, request)
      .then((r) => r.roll),
};
